import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { Actions, ofActionCompleted, Select, Store } from '@ngxs/store';
import { AlertBarService } from '@casb/alert-bar';
import { TableL10n, TableModule } from '@sym-ui/toolkit3/table';
import { SortEvent } from '@sym-ui/toolkit3/api';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { SessionState } from '@casb/session';
import {
  NotificationInstance,
  NotificationSetting,
  NotificationTableRow,
} from '../../notifications/src/notifications.model';
import { NotificationsDetailsComponent } from '../../notifications/src/components/notifications-details/notifications-details.component';
import { ColumnConfig, l10n } from '../../notifications/src/notifications-table.config';
import { AuditNotificationsActions } from '../store/audit-notifications.actions';
import { AuditNotificationsState } from '../store/audit-notifications.state';
import { AuditNotificationsStateModule } from '../store/audit-notifications.state.module';
import {
  AuditNotificationInstance,
  AuditNotificationTableRow,
  NotificationRowDefinition,
  auditNotificationDisplayName,
} from './audit-notifications.model';

@Component({
  selector: 'casb-audit-notifications',
  standalone: true,
  templateUrl: './audit-notifications.component.html',
  styleUrl: './audit-notifications.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    AuditNotificationsStateModule,
    NotificationsDetailsComponent,
    TableModule,
  ],
})
export class AuditNotificationsComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly alertBar = inject(AlertBarService);

  // eslint-disable-next-line no-restricted-syntax -- TODO(NGXS-select-migration): grandfathered, replace with inject(Store).select(...)
  @Select(AuditNotificationsState.columns) cols$!: Observable<ColumnConfig[]>;
  // eslint-disable-next-line no-restricted-syntax -- TODO(NGXS-select-migration): grandfathered, replace with inject(Store).select(...)
  @Select(AuditNotificationsState.tableRows) rows$!: Observable<AuditNotificationTableRow[]>;
  // eslint-disable-next-line no-restricted-syntax -- TODO(NGXS-select-migration): grandfathered, replace with inject(Store).select(...)
  @Select(AuditNotificationsState.loading) loading$!: Observable<boolean>;
  // eslint-disable-next-line no-restricted-syntax -- TODO(NGXS-select-migration): grandfathered, replace with inject(Store).select(...)
  @Select(AuditNotificationsState.savePending) savePending$!: Observable<boolean>;

  l10n: TableL10n = l10n;
  selectedColumns: ColumnConfig[] = [];
  rows: AuditNotificationTableRow[] = [];
  allowedDomains: string[] = [];

  selectedRow: NotificationTableRow | null = null;
  selectedInstance: NotificationInstance | null = null;
  selectedRowSettings: NotificationSetting[] = [];
  isFlyoutHidden = true;

  private sortField = '';
  private sortOrder = 1;
  private rowDefinitions: NotificationRowDefinition[] = [];
  private instance: AuditNotificationInstance | null = null;
  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    this.store.dispatch(new AuditNotificationsActions.LoadSummary());

    const session = this.store.selectSnapshot(SessionState.getSession);
    this.allowedDomains = session?.getAllDomains().filter(Boolean) ?? [];

    this.subscriptions.add(
      this.cols$.subscribe((cols) => {
        this.selectedColumns = cols.filter((c) => c.is_default);
      }),
    );

    this.subscriptions.add(
      this.rows$.subscribe((rows) => {
        this.rows = this.applySortOrder([...rows]);
      }),
    );

    this.subscriptions.add(
      this.store.select(AuditNotificationsState.rowDefinitions).subscribe((defs) => {
        this.rowDefinitions = defs;
      }),
    );

    this.subscriptions.add(
      this.store.select(AuditNotificationsState.instance).subscribe((instance) => {
        this.instance = instance;
        // Keep an open drawer's data fresh after a save response updates the store
        // (mirrors SpanVA, where onRowClick re-reads from the latest instances list).
        // selectedRow is typed as SpanVA's NotificationTableRow (see refreshSelection below),
        // so its id field is named spanva_id even though it holds this tenant's tenant_id.
        if (instance?.tenant_id && this.selectedRow?.spanva_id === instance.tenant_id) {
          this.refreshSelection(instance);
        }
      }),
    );

    this.subscriptions.add(
      this.actions$
        .pipe(
          ofActionCompleted(AuditNotificationsActions.LoadSummary),
          filter((r) => !!r.result.error),
        )
        .subscribe(() => {
          this.alertBar.errorAlert('Failed to load notification settings.');
        }),
    );

    this.subscriptions.add(
      this.actions$.pipe(ofActionCompleted(AuditNotificationsActions.Save)).subscribe((r) => {
        this.closeFlyout();
        if (r.result.error) {
          this.alertBar.errorAlert('Failed to save notification settings.');
        } else {
          this.alertBar.successAlert('Notification settings saved successfully.');
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onRowClick(row: AuditNotificationTableRow): void {
    // selectedRow.spanva_id holds this tenant's tenant_id — see refreshSelection below.
    const isSameRow = this.selectedRow?.spanva_id === row.tenant_id;
    if (isSameRow && !this.isFlyoutHidden) {
      this.closeFlyout();
      return;
    }
    if (!this.instance) {
      return;
    }
    this.refreshSelection(this.instance);
    this.isFlyoutHidden = false;
  }

  closeFlyout(): void {
    this.isFlyoutHidden = true;
    this.selectedRow = null;
    this.selectedInstance = null;
    this.selectedRowSettings = [];
  }

  onSaveNotification(event: {
    settings: NotificationSetting[];
    recipients: string[];
    masterEnabled: boolean;
  }): void {
    const payload = AuditNotificationsState.buildPatchPayload(
      this.rowDefinitions,
      event.settings,
      event.recipients,
      event.masterEnabled,
    );
    this.store.dispatch(new AuditNotificationsActions.Save(payload));
  }

  onColumnSelectionChange(selectedCols: ColumnConfig[]): void {
    this.selectedColumns = selectedCols;
  }

  onSort(event: SortEvent): void {
    this.sortField = event.field ?? '';
    this.sortOrder = event.order ?? 1;
    if (event.data) {
      event.data.sort((a: AuditNotificationTableRow, b: AuditNotificationTableRow) => {
        const valA = this.getCellValue(a, this.sortField);
        const valB = this.getCellValue(b, this.sortField);
        return valA.localeCompare(valB) * this.sortOrder;
      });
      this.rows = [...event.data];
    }
  }

  getCellValue(row: AuditNotificationTableRow, field: string): string {
    if (field === 'name') return row.name;
    const ch = row.channelsByUiId[field];
    if (!ch) return '—';
    return ch.enabled ? 'Enabled' : 'Disabled';
  }

  trackByTenantId(_index: number, row: AuditNotificationTableRow): string {
    return row.tenant_id;
  }

  // Adapts the tenant-wide singleton into the shapes NotificationsDetailsComponent expects
  // (it was built for SpanVA's per-instance model; the drawer itself is otherwise unchanged).
  private refreshSelection(instance: AuditNotificationInstance): void {
    const displayName = auditNotificationDisplayName(instance.tenant_name);
    // spanva_id is the shared model's row-id field name — holds tenant_id here, not a SpanVA id.
    this.selectedRow = {
      spanva_id: instance.tenant_id,
      name: displayName,
      master_enabled: instance.master_enabled,
      recipients: instance.recipients ?? [],
      channelsByUiId: {},
    };
    this.selectedInstance = {
      spanva_id: instance.tenant_id,
      agent_name: displayName,
      master_enabled: instance.master_enabled,
      recipients: instance.recipients ?? [],
      channels: instance.channels,
    };
    this.selectedRowSettings = this.rowDefinitions.map((def) => {
      const key = def.channel_key ?? def.channel_keys?.[0] ?? '';
      const ch = instance.channels[key];
      return {
        ui_id: def.ui_id,
        label: def.title,
        frequency_mode: def.frequency_mode,
        allowed_frequencies: def.allowed_frequencies,
        enabled: ch?.enabled ?? false,
        frequency: ch?.frequency ?? '',
      };
    });
  }

  private applySortOrder(rows: AuditNotificationTableRow[]): AuditNotificationTableRow[] {
    if (!this.sortField) return rows;
    return rows.sort((a, b) => {
      const valA = this.getCellValue(a, this.sortField);
      const valB = this.getCellValue(b, this.sortField);
      return valA.localeCompare(valB) * this.sortOrder;
    });
  }
}
